/**
 * Scalar-type editable cell inputs.
 * Handles: select, date, datetime, probability, number, boolean, textarea, text (default).
 * Select and boolean use auto-save; the rest use manual save via keydown/blur handlers.
 */

import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ResponsiveSelect } from "@/components/ui/responsive-select";
import { createAutoSaveHandler } from "../../../utils/inline-edit-controls";
import {
  createTextareaKeyDownHandler,
  createTextareaBlurSaveHandler,
} from "../../../utils/editable-cell-edit-handlers";
import {
  EDIT_INPUT_BASE_CLASS,
  EDIT_INPUT_DATE_CLASS,
  EDIT_INPUT_DATETIME_CLASS,
  EDIT_INPUT_NUMBER_CLASS,
  EDIT_SELECT_CLASS,
  EDIT_SLIDER_CLASS,
  EDIT_TEXTAREA_CLASS,
} from "../../../utils/editable-cell-edit-constants";
import { CalendarDateEdit } from "./CalendarDateEdit";
import { ProbabilitySliderEdit } from "./ProbabilitySliderEdit";
import type { EditableCellEditInputsProps } from "../types";
import type { BaseEntity } from "../../../types/index";

type ScalarInputsProps<T extends BaseEntity = BaseEntity> = Pick<
  EditableCellEditInputsProps<T>,
  | "value"
  | "fieldConfig"
  | "rowId"
  | "fieldId"
  | "editValue"
  | "data"
  | "onSave"
  | "onCancel"
  | "setEditValue"
  | "onInlineEdit"
  | "cellKey"
> & {
  keyDownHandler: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  blurSaveHandler: (e: React.FocusEvent) => void;
};

/** Select field input (auto-save). */
export function SelectInput<T extends BaseEntity = BaseEntity>({
  fieldConfig,
  rowId,
  fieldId,
  editValue,
  data,
  onInlineEdit,
  onCancel,
  setEditValue,
}: ScalarInputsProps<T>): React.ReactNode {
  const autoSaveParams = { rowId, fieldId, data, fieldConfig, onInlineEdit, onCancel, setEditValue };
  const selectOptions = (fieldConfig.options ?? [])
    .filter((o) => o.value !== "" && o.value != null)
    .map((o) => ({ value: String(o.value), label: o.label }));
  return (
    <ResponsiveSelect
      value={editValue}
      onValueChange={createAutoSaveHandler(autoSaveParams)}
      options={selectOptions}
      placeholder="Select..."
      className={EDIT_SELECT_CLASS}
    />
  );
}

/** Date field input (calendar picker). */
export function DateInput<T extends BaseEntity = BaseEntity>({
  value,
  editValue,
  setEditValue,
  onSave,
  onCancel,
  rowId,
  fieldId,
}: ScalarInputsProps<T>): React.ReactNode {
  return (
    <CalendarDateEdit
      value={typeof value === "string" ? value : value ? String(value) : undefined}
      editValue={editValue}
      setEditValue={setEditValue}
      onSave={onSave}
      onCancel={onCancel}
      rowId={rowId}
      fieldId={fieldId}
      className={EDIT_INPUT_DATE_CLASS}
    />
  );
}

/** Datetime-local input (manual save). */
export function DateTimeInput<T extends BaseEntity = BaseEntity>({
  editValue,
  setEditValue,
  keyDownHandler,
  blurSaveHandler,
}: ScalarInputsProps<T>): React.ReactNode {
  return (
    <Input
      type="datetime-local"
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      className={EDIT_INPUT_DATETIME_CLASS}
      autoFocus
      onKeyDown={keyDownHandler}
      onBlur={blurSaveHandler}
    />
  );
}

/** Probability slider + number input (manual save). */
export function ProbabilityInput<T extends BaseEntity = BaseEntity>({
  value,
  fieldId,
  editValue,
  setEditValue,
  onSave,
  onCancel,
  rowId,
  keyDownHandler,
  blurSaveHandler,
}: ScalarInputsProps<T>): React.ReactNode {
  return (
    <ProbabilitySliderEdit
      value={value}
      cellKey={fieldId}
      editValue={editValue}
      setEditValue={setEditValue}
      onSave={onSave}
      onCancel={onCancel}
      rowId={rowId}
      fieldId={fieldId}
      keyDownHandler={keyDownHandler}
      blurSaveHandler={blurSaveHandler}
      EDIT_SLIDER_CLASS={EDIT_SLIDER_CLASS}
      EDIT_INPUT_NUMBER_CLASS={EDIT_INPUT_NUMBER_CLASS}
    />
  );
}

/** Number input (manual save). */
export function NumberInput<T extends BaseEntity = BaseEntity>({
  editValue,
  setEditValue,
  keyDownHandler,
  blurSaveHandler,
}: ScalarInputsProps<T>): React.ReactNode {
  return (
    <Input
      type="number"
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      className={EDIT_INPUT_NUMBER_CLASS}
      autoFocus
      onKeyDown={keyDownHandler}
      onBlur={blurSaveHandler}
    />
  );
}

/** Boolean yes/no select (auto-save). */
export function BooleanInput<T extends BaseEntity = BaseEntity>({
  fieldConfig,
  rowId,
  fieldId,
  editValue,
  data,
  onInlineEdit,
  onCancel,
  setEditValue,
}: ScalarInputsProps<T>): React.ReactNode {
  const autoSaveParams = { rowId, fieldId, data, fieldConfig, onInlineEdit, onCancel, setEditValue };
  return (
    <ResponsiveSelect
      value={editValue}
      onValueChange={createAutoSaveHandler(autoSaveParams)}
      options={[
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ]}
      placeholder="Select..."
      className={EDIT_SELECT_CLASS}
    />
  );
}

/** Textarea input (manual save via Escape/blur). */
export function TextAreaInput<T extends BaseEntity = BaseEntity>({
  value,
  editValue,
  setEditValue,
  onSave,
  onCancel,
  rowId,
  fieldId,
}: ScalarInputsProps<T>): React.ReactNode {
  return (
    <Textarea
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      className={EDIT_TEXTAREA_CLASS}
      style={{ maxHeight: "100%" }}
      autoFocus
      onKeyDown={createTextareaKeyDownHandler({ onCancel })}
      onBlur={createTextareaBlurSaveHandler({ onSave, onCancel, rowId, fieldId, value, editValue })}
    />
  );
}

/** Default text input (manual save). */
export function TextInput<T extends BaseEntity = BaseEntity>({
  editValue,
  setEditValue,
  keyDownHandler,
  blurSaveHandler,
}: ScalarInputsProps<T>): React.ReactNode {
  return (
    <Input
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      className={EDIT_INPUT_BASE_CLASS}
      autoFocus
      onKeyDown={keyDownHandler}
      onBlur={blurSaveHandler}
    />
  );
}
